import { httpClient } from '@/core/api/httpClient';
import type { ClientContact, ClientContactDto } from '../types';
import type { CreateClientContactPayload, UpdateClientContactPayload } from '../types';
import { adaptClientContactDto } from '../adapters/client-contact.adapter';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

const contactsMock: ClientContact[] = [
  {
    id: '1',
    name: 'María García',
    email: 'maria@constructoraalfa.mx',
    phone: '+52 55 1234 5678',
    isPrimary: true,
    createdAt: '2024-01-10T08:00:00Z',
  },
  {
    id: '2',
    name: 'Carlos López',
    email: 'carlos@constructoraalfa.mx',
    phone: '+52 55 9876 5432',
    isPrimary: false,
    createdAt: '2024-01-15T10:30:00Z',
  },
  {
    id: '3',
    name: 'Ana Martínez',
    email: 'ana@distribuidorabeta.mx',
    phone: '+52 33 9876 5432',
    isPrimary: true,
    createdAt: '2024-02-14T10:30:00Z',
  },
];

const clientIdsMock = ['1', '2'];

export const clientContactsService = {
  getAll: async (clientId: string, signal?: AbortSignal): Promise<ClientContact[]> => {
    if (USE_MOCK) {
      if (!clientIdsMock.includes(clientId)) return [];
      return contactsMock.filter((_, i) => i < (clientId === '1' ? 2 : 1));
    }

    const { data } = await httpClient.get<ClientContactDto[]>(
      `${API_V1}/tenants/clients/${clientId}/contacts`,
      { signal },
    );
    return data.map(adaptClientContactDto);
  },

  getOne: async (
    clientId: string,
    contactId: string,
    signal?: AbortSignal,
  ): Promise<ClientContact> => {
    if (USE_MOCK) {
      const contact = contactsMock.find((c) => c.id === contactId);
      if (!contact) throw new Error('Contact not found');
      return contact;
    }

    const { data } = await httpClient.get<ClientContactDto>(
      `${API_V1}/tenants/clients/${clientId}/contacts/${contactId}`,
      { signal },
    );
    return adaptClientContactDto(data);
  },

  create: async (clientId: string, payload: CreateClientContactPayload): Promise<ClientContact> => {
    if (USE_MOCK) {
      const newContact: ClientContact = {
        id: `contact-${Date.now()}`,
        name: payload.name,
        email: payload.email ?? '',
        phone: payload.phone ?? '',
        isPrimary: payload.isPrimary,
        createdAt: new Date().toISOString(),
      };
      contactsMock.push(newContact);
      return newContact;
    }

    const { data } = await httpClient.post<CreateClientContactPayload, ClientContactDto>(
      `${API_V1}/tenants/clients/${clientId}/contacts`,
      payload,
    );
    return adaptClientContactDto(data);
  },

  update: async (
    clientId: string,
    contactId: string,
    payload: UpdateClientContactPayload,
  ): Promise<ClientContact> => {
    if (USE_MOCK) {
      const index = contactsMock.findIndex((c) => c.id === contactId);
      if (index === -1) throw new Error('Contact not found');
      const updated = { ...contactsMock[index], ...payload };
      contactsMock[index] = updated;
      return updated;
    }

    await httpClient.put<UpdateClientContactPayload, void>(
      `${API_V1}/tenants/clients/${clientId}/contacts/${contactId}`,
      payload,
    );

    return clientContactsService.getOne(clientId, contactId);
  },

  delete: async (clientId: string, contactId: string): Promise<void> => {
    if (USE_MOCK) {
      const index = contactsMock.findIndex((c) => c.id === contactId);
      if (index !== -1) contactsMock.splice(index, 1);
      return;
    }

    await httpClient.delete(`${API_V1}/tenants/clients/${clientId}/contacts/${contactId}`);
  },
};
